import { Injectable } from '@angular/core';
import { LoggerService } from 'src/app/core/services/logger.service';
import { UtilService } from 'src/app/core/services/util.service';
import { GenericCreateRecordDynamics } from 'src/app/shared/models/main-dynamics/genericCreateRecordDynamics';
import { GenericCreateSessionDynamics } from 'src/app/shared/models/main-dynamics/genericCreateSessionDynamics';
import { GenericDataSessionDynamicsDTO } from 'src/app/shared/models/main-dynamics/genericDataSessionDynamicsDTO';
import { GenericTemplateValuesChannelProviderDynamics } from 'src/app/shared/models/main-dynamics/genericTemplateValuesChannelProviderDynamics';
import { GenericTicketResultDynamics } from 'src/app/shared/models/main-dynamics/genericTicketResultDynamics';
import {
  Central,
  CentralConfig,
  CentralRetrieveConfigFactory,
} from './dynamics-retrieve-config';

@Injectable({
  providedIn: 'root',
})
export class DynamicsMainService {
  public templateValues: GenericTemplateValuesChannelProviderDynamics = {
    fieldNegocio: '',
    retrieveRecordCol: '',
    retrieveRecordTable: '',
    templateAusente: '',
    templateCreateSession: '',
    templateSessaoPadrao: '',
    triggerPlugin: '',
    itau_name: '',
    itau_u_lk: '',
    itau_u_nomeprojeto: '',
  };

  retrieveConfig: CentralConfig = {
    retrieveColQuery: '',
    retrieveFieldQuery: '',
    retrieveQueryUrl: '',
  };

  constructor(
    private logger: LoggerService,
    private utilService: UtilService,
  ) {
    this.loadTemplateValues();
  }
  public async initialize(cifUrl: string, REDES: Central): Promise<void> {
    this.logger.info(`DYNAMICS - inicializando CIF: ${cifUrl}`);
    this.utilService.getIframeEnvironment();
    const script = document.createElement('script');

    script.src = cifUrl;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-crmurl', cifUrl);
    script.setAttribute('data-cifid', 'CIFMainLibrary');

    document.body.appendChild(script);

    return new Promise<void>((resolve, reject) => {
      const onCIFInitDone = async (event: Event) => {
        this.logger.info('DYNAMICS - CIF inicializado', event);
        try {
          this.logger.info('DYNAMICS - CIF Iframe setado para 400 pixels');

          await (window as any).Microsoft.CIFramework.setMode(1);

          await (window as any).Microsoft.CIFramework.addHandler(
            'onmodechanged',
            async (eventData: any) => {
              console.log('DYNAMICS - CIF Mode Changed:', eventData);

              await (window as any).Microsoft.CIFramework.setMode(1);
            },
          );

          await (window as any).Microsoft.CIFramework.setWidth(400);
          await this.configRetrieveRecord(REDES);
        } catch (error) {
          this.logger.error('DYNAMICS - Erro ao inicializar CIF', error);
          reject(error);
        } finally {
          resolve();
          window.removeEventListener('CIFInitDone', onCIFInitDone);
        }
      };

      window.addEventListener('CIFInitDone', onCIFInitDone);
    });
  }

  public async openTicket(
    createRecordDTO: GenericCreateRecordDynamics,
  ): Promise<GenericTicketResultDynamics> {
    const retorno: GenericTicketResultDynamics = {
      id_interacao: null,
      id_ticket: null,
    };

    try {
      this.logger.info('AttachData - ', createRecordDTO.data);
      retorno.id_interacao = await this.createInteraction(createRecordDTO);

      if (retorno.id_ticket == null && retorno.id_interacao != null) {
        retorno.id_ticket = await this.retrieveTicketId(
          retorno.id_interacao,
          createRecordDTO.tableName,
        );
        this.logger.info(`DYNAMICS - Ticket recuperado: ${retorno.id_ticket}`);

        const sessionData: GenericCreateSessionDynamics = {
          templateName: this.templateValues.templateCreateSession,
          templateParameters: {
            id_interacao: retorno.id_interacao,
            id_ticket: retorno.id_ticket,
          },
          isFocused: createRecordDTO.isFocused
            ? createRecordDTO.isFocused
            : true,
        };

        const sessionId = await this.createSession(sessionData);

        const sessionRelationship: GenericDataSessionDynamicsDTO = {
          dynamicsinteractionId: retorno.id_interacao,
          dynamicsSessionId: sessionId,
          contactCenterInteractionId:
            createRecordDTO.contactCenterInteractionId,
        };

        this.addSessionRelationship(sessionRelationship);
      }
    } catch (error) {
      throw new Error(
        `Erro ao criar interação ou recuperar ticket: ${error instanceof Error ? error.message : error}`,
      );
    }
    return retorno;
  }

  public async closeTicket(
    context: any,
    contactCenterId: string,
  ): Promise<void> {
    this.logger.info('Fechando o ticket no dynamics - ', contactCenterId);

    try {
      const session = this.getSessionRelationship(contactCenterId);
      await (window as any).Microsoft.CIFramework.updateContext(
        context,
        session?.dynamicsSessionId,
      );
    } catch (error) {
      this.logger.error('Error ao fechar o ticket ', error);
      throw error;
    }
  }

  public async closeTicketRaiseEvent(
    contactCenterId: string,
    funcitonaName: string,
  ): Promise<void> {
    this.logger.info(
      'Fechando o ticket no dynamics pelo raiseEvent -',
      contactCenterId,
    );
    try {
      const session = this.getSessionRelationship(contactCenterId);
      await (window as any).Microsoft.CIFramework.raiseEvent(funcitonaName, {
        sessionId: session?.dynamicsSessionId,
        correlationId: session?.dynamicsinteractionId,
      });
    } catch (error) {
      this.logger.error('Error ao fechar o ticket ', error);
    }
  }

  public async changeSessionFocus(
    contactCenterInteractionId: string,
    raiseEventName: string,
  ): Promise<void> {
    localStorage.setItem('interactionFocused', contactCenterInteractionId);

    const sessionRelationship = this.getSessionRelationship(
      contactCenterInteractionId,
    );

    await (window as any).Microsoft.CIFramework.raiseEvent(raiseEventName, {
      sessionId: sessionRelationship?.dynamicsSessionId,
      correlationId: sessionRelationship?.dynamicsinteractionId,
    });
  }

  private async createInteraction(
    createRecordDTO: GenericCreateRecordDynamics,
  ) {
    try {
      this.logger.info(
        'Solicitando criação de interação.',
        JSON.stringify(createRecordDTO.data),
      );
      const createRecordResult = await (
        window as any
      ).Microsoft.CIFramework.createRecord(
        createRecordDTO.tableName,
        JSON.stringify(createRecordDTO.data),
      );
      this.logger.info(
        'Criação de interação realizada com sucesso.',
        createRecordResult,
      );
      return JSON.parse(createRecordResult).id;
    } catch (error) {
      this.logger.error('Erro ao criar interação.', error);
      throw new Error(
        `Erro ao criar interação: ${error instanceof Error ? error.message : error}`,
      );
    }
  }

  private async retrieveTicketId(
    interactionId: string,
    tableName: string,
  ): Promise<string> {
    this.logger.info(
      'Recuperando ID do Ticket para a interação.',
      interactionId,
    );

    const RECALL_LIMIT = 40;
    const RECALL_DELAY_MS = 2000;

    let idTicket;
    for (let attempt = 0; attempt < RECALL_LIMIT; attempt++) {
      this.logger.info('Tentativa', attempt + 1);
      await this.delay(RECALL_DELAY_MS);

      try {
        const resultTicket = await (
          window as any
        ).Microsoft.CIFramework.retrieveRecord(
          tableName,
          interactionId,
          '?$select=itau_interacaoid&$expand=rede_atendimento_interacaoid_itau_interacao($select=rede_atendimentoid)',
        );

        const interaction = JSON.parse(resultTicket);
        this.logger.info('interaction: ', interaction);

        if (
          interaction['rede_atendimento_interacaoid_itau_interacao']?.[0] !=
          null
        ) {
          if (
            interaction['rede_atendimento_interacaoid_itau_interacao'][0]
              .rede_atendimentoid != null
          ) {
            idTicket =
              interaction['rede_atendimento_interacaoid_itau_interacao'][0]
                .rede_atendimentoid;
            console.log('Id Ticket recuperado com sucesso: ', idTicket);
            return idTicket;
          }
        }
      } catch (e) {
        this.logger.warn('Falha na tentativa: ', attempt, e);
      }
    }
    throw new Error(`Erro ao recuperar ID do Ticket.`);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async createSession(
    createSessionDynamics: GenericCreateSessionDynamics,
  ) {
    this.logger.info('Criando sessão com o template', createSessionDynamics);
    try {
      const sessionId = await (
        window as any
      ).Microsoft.CIFramework.createSession(createSessionDynamics);
      this.logger.info('Sessão criada com sucesso ', sessionId);
      return sessionId;
    } catch (error) {
      this.logger.error('Erro ao criar sessão ', error);
      throw new Error(
        `Erro ao criar sessão: ${error instanceof Error ? error.message : error}`,
      );
    }
  }

  public async loadTemplateValues(): Promise<void> {
    const channelProviderVariablesRaw = await (
      window as any
    ).Microsoft.CIFramework.getEnvironment();

    this.logger.info('channel provider raw', channelProviderVariablesRaw);
    if (channelProviderVariablesRaw) {
      const channelProviderVariables = JSON.parse(channelProviderVariablesRaw);
      this.logger.info(
        'channel provider obj inside if',
        channelProviderVariables,
      );

      let customParams: any = {};
      if (channelProviderVariables.msdyn_customparams) {
        customParams = Object.fromEntries(
          channelProviderVariables.msdyn_customparams
            .split('&')
            .map((kv: string) => kv.split('=')),
        );
      }
      this.logger.info('customparams', customParams);
      this.templateValues.templateCreateSession =
        customParams.templateCreateSession || null;
      this.templateValues.templateSessaoPadrao =
        customParams.templateSessaoPadrao || null;
      this.templateValues.templateAusente =
        customParams.templateAusente || null;
      this.templateValues.fieldNegocio = customParams.fieldNegocio || null;
      this.templateValues.triggerPlugin = customParams.triggerPlugin || null;
      this.templateValues.retrieveRecordTable =
        customParams.retrieveRecordTable || null;
      this.templateValues.retrieveRecordCol =
        customParams.retrieveRecordCol || null;
      this.templateValues.itau_u_nomeprojeto =
        customParams.itau_u_nomeprojeto || null;
      this.templateValues.itau_u_lk = customParams.itau_u_lk || null;
      this.templateValues.itau_name = customParams.itau_name || null;

      this.logger.info(
        'DYNAMICS - Template Values Loaded:',
        this.templateValues,
      );
    } else {
      this.logger.error(
        'Erro ao carregar environment do CIFramework:',
        channelProviderVariablesRaw,
      );
    }
  }

  private addSessionRelationship(
    sessionData: GenericDataSessionDynamicsDTO,
  ): void {
    localStorage.setItem(
      `sessionRelationship-${sessionData.contactCenterInteractionId}`,
      JSON.stringify(sessionData),
    );
  }

  private getSessionRelationship(
    ContactCenterInteractionId: string,
  ): GenericDataSessionDynamicsDTO | null {
    const value = localStorage.getItem(
      `sessionRelationship-${ContactCenterInteractionId}`,
    );
    if (value) {
      return JSON.parse(value);
    }
    return null;
  }

  public async createSessionPadrao() {
    this.logger.info('Criando sessão padrao');
    if (this.templateValues.templateSessaoPadrao == '') {
      await this.loadTemplateValues();
    }
    const sessionData: GenericCreateSessionDynamics = {
      templateName: null,
      templateParameters: {},
      isFocused: true,
    };
    this.logger.info('Criando a sessão padrão com o template', sessionData);
    try {
      const sessionId = await (
        window as any
      ).Microsoft.CIFramework.createSession(sessionData);
      this.logger.info('Sessão criada com sucesso ', sessionId);
      return sessionId;
    } catch (error) {
      this.logger.error('Erro ao criar sessão ', error);
      throw new Error(
        `Erro ao criar sessão: ${error instanceof Error ? error.message : error}`,
      );
    }
  }

  private async configRetrieveRecord(central: Central) {
    this.logger.info('buscando config de retrive records');
    const config = CentralRetrieveConfigFactory.getConfig(central);
    this.logger.info('retrieveConfig -> ', config);
    this.retrieveConfig = config;
    return;
  }
}
